import { z } from 'zod';
import {
  AbilityNameSchema,
  ArmorCategorySchema,
  DieTypeSchema,
  SkillNameSchema,
  SourceSchema,
  WeaponCategorySchema,
} from './common.schema';

const FeatureChoiceSchema = z.object({
  type: z.enum([
    'skill',
    'fighting-style',
    'expertise',
    'maneuver',
    'invocation',
    'metamagic',
    'other',
  ]),
  count: z.number(),
  from: z.array(z.string()),
});

const ClassFeatureSchema = z.object({
  id: z.string(),
  name: z.string(),
  level: z.number(),
  description: z.string(),
  choices: z.array(FeatureChoiceSchema).optional(),
});

const SpellcastingProgressionSchema = z.object({
  type: z.enum(['full', 'half', 'third', 'pact']),
  ability: AbilityNameSchema,
  ritual: z.boolean(),
  focus: z.string().optional(),
  cantripsKnown: z.array(z.number()).optional(),
  spellsKnown: z.array(z.number()).optional(),
  spellSlots: z.array(z.array(z.number())),
});

const SubclassSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  features: z.array(ClassFeatureSchema),
  spellcasting: SpellcastingProgressionSchema.optional(),
});

const StartingEquipmentChoiceSchema = z.object({
  choose: z.number(),
  from: z.array(z.array(z.string())),
});

const StartingEquipmentItemSchema = z.union([z.string(), StartingEquipmentChoiceSchema]);

const MulticlassRequirementSchema = z.object({
  ability: AbilityNameSchema,
  minimum: z.number(),
});

const MulticlassProficienciesSchema = z.object({
  armor: z.array(ArmorCategorySchema),
  weapons: z.array(z.union([WeaponCategorySchema, z.string()])),
  skills: z
    .object({
      count: z.number(),
      from: z.array(SkillNameSchema),
    })
    .optional(),
});

export const ClassSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  hitDie: DieTypeSchema,
  primaryAbility: z.array(AbilityNameSchema),
  savingThrows: z.array(AbilityNameSchema),
  armorProficiencies: z.array(ArmorCategorySchema),
  weaponProficiencies: z.array(z.union([WeaponCategorySchema, z.string()])),
  toolProficiencies: z.array(z.string()),
  toolProficiencyChoices: z
    .object({
      count: z.number(),
      from: z.array(z.string()),
    })
    .optional(),
  skillChoices: z.object({
    count: z.number(),
    from: z.array(SkillNameSchema),
  }),
  startingEquipment: z.array(StartingEquipmentItemSchema),
  features: z.array(ClassFeatureSchema),
  spellcasting: SpellcastingProgressionSchema.optional(),
  subclassLevel: z.number(),
  subclassName: z.string(),
  subclasses: z.array(SubclassSchema),
  multiclassRequirements: z.array(MulticlassRequirementSchema).optional(),
  multiclassRequirementsLogic: z.enum(['and', 'or']).optional(),
  multiclassProficiencies: MulticlassProficienciesSchema.optional(),
  source: SourceSchema,
});

export type ClassData = z.infer<typeof ClassSchema>;
